import 'react-native-get-random-values'

import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import * as WebBrowser from 'expo-web-browser'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useReducer } from 'react'
import { Image, Linking, Platform, View, ActivityIndicator } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import Animated, { FadeOut } from 'react-native-reanimated'
import { v4 as uuidv4 } from 'uuid'
import * as AuthSession from 'expo-auth-session'

import logo from '../../../assets/Onboarding/logo.png'
import { Button, Text } from '../../components'
import { translate } from '../../i18n'
import { broadcastChannel, rootStore, useStore } from '../../models'
import { startStyles } from './style'
import { DS } from '../../design-system'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import { Href, useLocalSearchParams, useRouter } from 'expo-router'
import { useInitialLinkingStore } from '@/hooks/useInitialLinkingStore'
import { auth0Config } from '@/config/auth0'
import { WebWrapper } from '@/components/WebWrapper'
import { onSignInSuccess } from '@/screens/sign-up/onSignInSuccess'
import { useSessionStore } from '@/models/session/useSessionStore'

const logger = extendLogger(LoggerContexts.SignUpScreen)
WebBrowser.maybeCompleteAuthSession()
const isWeb = Platform.OS === 'web'

export const SignUp = observer(function SignUp() {
  const router = useRouter()
  const store = useStore()
  const [loading, setLoading] = React.useState(false)

  const { prompt, auto, screen_hint } = useLocalSearchParams<{
    prompt?: AuthSession.Prompt
    auto?: 'true' | 'false'
    screen_hint: string
  }>()

  const discovery = AuthSession.useAutoDiscovery(auth0Config.discoveryUrl)

  logger.log('LOG: > SignUp > discovery:', discovery)

  const clientUrl = window.location.href
  const redirectUri = AuthSession.makeRedirectUri({
    native: clientUrl,
    path: '/login'
    // path: ''
  })

  React.useEffect(() => {
    store.share.referBranchSubscription()
  }, [store.share])

  const [isLoading, finishLoading] = useReducer((s) => !s, true)

  const [request, response, promptAsync] = AuthSession.useAuthRequest(
    {
      clientId: auth0Config.clientId,
      redirectUri,
      // responseType: 'token',
      scopes: auth0Config.scopes,
      extraParams: {
        audience: auth0Config.audience,
        access_type: 'offline',
        screen_hint
      },
      prompt: prompt ?? AuthSession.Prompt.Login
    },
    discovery
  )

  useEffect(() => {
    if (auto === 'true' && request && discovery) {
      handleLogin().then(() => {
        router.setParams({ auto: 'false' })
      })
    }
  }, [request, discovery, auto, router])

  const handleLogin = async () => {
    try {
      setLoading(true)
      store.analytics.setEvent('Login Initiated', {
        loginType: 'auth0'
      })
      const result = await promptAsync({
        windowFeatures: {
          width: 800,
          height: 800,
          createTask: false
        }
      })
      if (!result.type || result.type === 'error') {
        logger.error('AuthSession error', { result })
        throw result.error
      }

      if (result.type === 'success' && request?.codeVerifier && discovery) {
        const { code } = result.params
        const codeRes: AuthSession.TokenResponse = await AuthSession.exchangeCodeAsync(
          {
            code,
            redirectUri,
            clientId: auth0Config.clientId,
            extraParams: {
              code_verifier: request?.codeVerifier
            }
          },
          discovery
        )

        const tokenConfig = codeRes?.getRequestConfig()

        const accessToken = tokenConfig.accessToken

        useSessionStore.getState().setTokenConfig(tokenConfig)
        await store.session.fetchMe()
        store.session.mergeAnonymousUserId()
        onSignInSuccess()
        // Redirect to initial url or root
        const initialUrl = useInitialLinkingStore.getState().url
        isWeb && broadcastChannel?.postMessage({ type: 'login' })

        if (initialUrl) {
          useInitialLinkingStore.getState().dispose('url')
          const fullHref = initialUrl.search
            ? `${initialUrl.pathname}${initialUrl.search}`
            : initialUrl.pathname
          store.analytics.setEvent('Deep Link Consumed', { url: fullHref, source: 'SignUpWeb' })
          router.replace(fullHref as Href)
          return
        }
        router.replace('/')
      }
    } catch (error) {
      logger.error('handle login error: ', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <View style={{ flex: 1 }}>
      {isLoading ? (
        <Animated.View
          exiting={FadeOut.delay(1000).duration(500)}
          style={{
            flex: 1,
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: '#7B043D'
          }}
        >
          <Image
            key={'SplashAnimation'}
            source={require('../../../assets/SplashScreen/pokerPowerSplashscreen.gif')}
            style={{ flex: 1, alignSelf: 'center' }}
            resizeMode="cover"
            onLoadEnd={finishLoading}
            onError={finishLoading}
          />
        </Animated.View>
      ) : null}

      <SafeAreaView style={startStyles.container1} />
      <SafeAreaView style={startStyles.container2}>
        <StatusBar animated={true} style={'light' as StatusBarStyle} hidden={false} />
        <React.Fragment>
          <View style={{ flex: 2, backgroundColor: '#7B043D' }}>
            <Image
              style={{ height: 36, width: 200, alignSelf: 'center', marginTop: 32, position: 'absolute' }}
              source={logo}
              resizeMode="contain"
            />
            <WebWrapper>
              <View
                style={[
                  {
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }
                ]}
              >
                <View
                  style={{
                    zIndex: 9,
                    position: 'absolute',
                    left: 0,
                    transform: [
                      {
                        translateX: -20
                      },
                      {
                        translateY: -180
                      },
                      {
                        rotate: '15deg'
                      }
                    ]
                  }}
                >
                  <Image
                    source={require('../../assets/poker-power-chip-pink.png')}
                    style={{
                      width: 72,
                      height: 72
                    }}
                  />
                </View>
                <Image
                  source={require('../../../assets/Onboarding/suite-icons.png')}
                  style={startStyles.firstOnboardSuitIcon}
                />
                <View style={{ marginTop: 16, paddingHorizontal: 32 }}>
                  <Text style={startStyles.firstOnboardText}>Join 27,000+ women</Text>
                  <Text style={[startStyles.firstOnboardText]}>
                    {'building '}
                    <Text style={[startStyles.firstOnboardText, { fontStyle: 'italic' }]}>confidence,</Text>
                  </Text>
                  <Text style={[startStyles.firstOnboardText]}>
                    <Text style={[startStyles.firstOnboardText, { fontStyle: 'italic' }]}>strategy,</Text>
                    {' and '}
                    <Text style={[startStyles.firstOnboardText, { fontStyle: 'italic' }]}>community</Text>
                  </Text>
                  <Text style={startStyles.firstOnboardText}>through poker</Text>
                </View>
                <View
                  style={{
                    position: 'absolute',
                    right: 0,
                    transform: [
                      {
                        translateY: 180
                      }
                    ]
                  }}
                >
                  <View
                    style={{
                      zIndex: 9,
                      transform: [
                        {
                          translateX: 24
                        },
                        {
                          rotate: '-15deg'
                        }
                      ]
                    }}
                  >
                    <Image
                      source={require('../../assets/poker-power-chip-pink.png')}
                      style={{
                        width: 72,
                        height: 72
                      }}
                    />
                  </View>
                  <View
                    style={{
                      zIndex: 9,
                      transform: [
                        {
                          translateX: -16
                        },
                        {
                          translateY: -40
                        },
                        {
                          rotate: '30deg'
                        }
                      ]
                    }}
                  >
                    <Image
                      source={require('../../assets/poker-power-chip-pink.png')}
                      style={{
                        width: 72,
                        height: 72
                      }}
                    />
                  </View>
                </View>
              </View>
            </WebWrapper>
          </View>

          <View style={[startStyles.middleView, { flex: 1, justifyContent: 'center' }]}>
            {loading ? (
              <View
                style={{
                  width: '35%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 50,
                  borderRadius: 32,
                  backgroundColor: '#F6F2E9',
                  borderWidth: 1,
                  borderColor: '#DDDAD2'
                }}
              >
                <ActivityIndicator size="small" color="#000000" />
              </View>
            ) : (
              <DS.Button variant="Large/Secondary" type="On Dark" onPress={handleLogin} style={{ width: '35%' }}>
                Get Started
              </DS.Button>
            )}
            <View style={{ marginVertical: 15 }} />

            <Text style={startStyles.bottomText} allowFontScaling={false}>
              {translate('signup.bottomText')}
              <Text
                style={startStyles.termsAndPrivacyText}
                allowFontScaling={false}
                onPress={async () => {
                  const url = 'https://pokerpower.com/terms-of-service/'
                  try {
                    await WebBrowser.openBrowserAsync(url)
                  } catch (error) {
                    Linking.openURL(url)
                  }
                }}
              >
                {translate('signup.termsOfUse')}
              </Text>
              {translate('signup.and')}
              <Text
                allowFontScaling={false}
                style={startStyles.termsAndPrivacyText}
                onPress={async () => {
                  const url = 'https://pokerpower.com/privacy-policy/'
                  try {
                    await WebBrowser.openBrowserAsync(url)
                  } catch (error) {
                    Linking.openURL(url)
                  }
                }}
              >
                {translate('signup.privacyPolicy')}
              </Text>
            </Text>
          </View>

          {/* Play as Guest Functionality
            <View>
              <Text style={startStyles.orText} >or</Text>
            </View>
            <TouchableOpacity
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onPress={debounce(onGuestButtonPress, 250)}
            >
              <Text style={startStyles.guestText} >Play as Guest</Text>
              <View
                style={{
                  width: '38%',
                  height: 2,
                  borderRadius: 6,
                  backgroundColor: 'white',
                }}
              />
            </TouchableOpacity> */}
        </React.Fragment>
      </SafeAreaView>
    </View>
  )
})
